import React from "react";
import { Space } from "antd";
import { ThemeProvider as StyledThemeProvider, DefaultTheme } from "styled-components";
import { UserOutlined, SafetyOutlined } from "@ant-design/icons";
import { useTheme } from "../../context/ThemeContext";
import {
  StyledModal,
  ModalFooter,
  CloseButton,
  SubmitButton,
  ModalContentWrapper,
  SectionCard,
  SectionTitle,
  InfoItem,
  RolesList,
  RoleTag,
  UserAvatar,
  UserDetails,
} from "./Styles";
import { UserDetailsModalProps } from "./types";

const UserDetailsModal: React.FC<UserDetailsModalProps> = ({ visible, onClose, user, onEditRole }) => {
  const { isDarkMode } = useTheme();
  if (!user) return null;

  const handleClose = () => {
    onClose();
  };

  const handleEditRole = () => {
    onClose();
    onEditRole(user);
  };

  const getUserInitials = (name: string | undefined, username: string | undefined, email: string | undefined) => {
    if (name) {
      const parts = name.split(" ");
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
      }
      return name[0].toUpperCase();
    }
    if (username) {
      return username[0].toUpperCase();
    }
    if (email) {
      return email[0].toUpperCase();
    }
    return "U";
  };

  const styledTheme: DefaultTheme = { mode: isDarkMode ? "dark" : "light" };

  return (
    <StyledThemeProvider theme={styledTheme}>
    <StyledModal
      title={
        <Space>
          <UserOutlined />
          <span>User Details</span>
        </Space>
      }
      open={visible}
      onCancel={handleClose}
      destroyOnClose
      zIndex={2000}
      footer={
        <ModalFooter>
          <CloseButton onClick={handleClose}>Close</CloseButton>
          <SubmitButton type="primary" onClick={handleEditRole}>
            Update Role
          </SubmitButton>
        </ModalFooter>
      }
      width={700}
    >
      <ModalContentWrapper>
        <SectionCard>
          <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "24px" }}>
            <UserAvatar size={64}>
              {getUserInitials(user.name, user.username, user.email)}
            </UserAvatar>
            <UserDetails>
              <div style={{ fontSize: "20px", fontWeight: 600, color: isDarkMode ? "#fff" : "#1a1a1a", marginBottom: "4px" }}>
                {user.name || user.username || user.email}
              </div>
              {user.email && (
                <div style={{ fontSize: "14px", color: isDarkMode ? "#bfbfbf" : "#8c8c8c" }}>
                  {user.email}
                </div>
              )}
            </UserDetails>
          </div>
        </SectionCard>

        <SectionCard>
          <SectionTitle level={5}>User Information</SectionTitle>
          <InfoItem>
            <strong>Name:</strong> <span>{user.name || user.email || "-"}</span>
          </InfoItem>
          <InfoItem>
            <strong>Email:</strong> <span>{user.email || "-"}</span>
          </InfoItem>
          <InfoItem>
            <strong>Phone:</strong> <span>{user.phone || "-"}</span>
          </InfoItem>
          <InfoItem>
            <strong>Status:</strong>{" "}
            <span>{user.is_active !== false ? "Active" : "Inactive"}</span>
          </InfoItem>
        </SectionCard>

        <SectionCard>
          <SectionTitle level={5}>Assigned Roles</SectionTitle>
          {user.role ? (
            <RolesList>
              <RoleTag>
                <SafetyOutlined />
                <span>{user.role}</span>
              </RoleTag>
            </RolesList>
          ) : user.roles && user.roles.length > 0 ? (
            <RolesList>
              {user.roles.map((role) => (
                <RoleTag key={role.id}>
                  <SafetyOutlined />
                  <span>{role.name}</span>
                  {role.is_system_role && (
                    <span className="system-label">(System)</span>
                  )}
                </RoleTag>
              ))}
            </RolesList>
          ) : (
            <div style={{ color: isDarkMode ? "#bfbfbf" : "#8c8c8c", fontStyle: "italic" }}>No roles assigned</div>
          )}
        </SectionCard>
      </ModalContentWrapper>
    </StyledModal>
    </StyledThemeProvider>
  );
};

export default UserDetailsModal;


